import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { Title } from "@/components/Title";
import { Icon } from "@/components/Icon";
import { tasksApi, taskStatusesApi, sprintsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { NewTaskModal } from "@/pages/Tasks/NewTaskModal";
import { TaskDetailModal } from "@/pages/Tasks/TaskDetailModal";
import { TaskViewModal } from "@/pages/Tasks/TaskViewModal";
import { Tabs } from "@/components/Tabs";
import { StatCard } from "@/components/StatCard";
import { TasksTable } from "@/pages/Tasks/TasksTable";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Select } from "@/components/Select";
import { SearchInput } from "@/components/SearchInput";
import { KanbanColumn } from "@/pages/Tasks/KanbanColumn";
import { SprintsSettings } from "@/pages/Tasks/SprintsSettings";
import { TaskStatusesSettings } from "@/pages/Tasks/TaskStatusesSettings";
import { ChangeTaskPriorityModal } from "@/pages/Tasks/ChangeTaskPriorityModal";
import { ChangeTaskAssigneeModal } from "@/pages/Tasks/ChangeTaskAssigneeModal";
import { PageHeader } from "@/components/PageHeader";
import { parseListSelection } from "@/components/listPreferences";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { z } from "zod";

const getInitialTab = (): string => {
    if (typeof window !== "undefined") {
        const path = window.location.pathname.replace(/\/+$/, "");
        const segments = path.split("/");

        if (segments.includes("kanban")) return "kanban";
        if (segments.includes("settings")) return "settings";
        if (segments.includes("table")) return "table";

        const searchParams = new URLSearchParams(window.location.search);
        const queryTab = searchParams.get("tab");
        if (queryTab && ["table", "kanban", "settings"].includes(queryTab)) {
            return queryTab;
        }
    }

    return "table";
};

const getInitialStateTab = (): string => {
    if (typeof window !== "undefined") {
        const path = window.location.pathname.replace(/\/+$/, "");
        const segments = path.split("/");

        if (segments.includes("closed")) return "closed";
        if (segments.includes("all")) return "all";
        if (segments.includes("open")) return "open";
    }
    return "open";
};

const getInitialSubTab = (): "sprints" | "statuses" => {
    if (typeof window !== "undefined") {
        const path = window.location.pathname.replace(/\/+$/, "");
        const seg = path.split("/").pop() || "";
        if (seg === "statuses") return "statuses";
    }
    return "sprints";
};

export const Tasks = () => {
    const { t } = useTranslation("global");
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { showError } = useToast();
    const [statuses, setStatuses] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
    const [settingsSubTab, setSettingsSubTab] = useState<
        "sprints" | "statuses"
    >(getInitialSubTab);
    const [activeSprint, setActiveSprint] = useState<any>(null);
    const [prefilledStatusId, setPrefilledStatusId] = useState(null);
    const [prefilledSprintId, setPrefilledSprintId] = useState<number | null>(
        null
    );
    const [activeTab, setActiveTab] = useState<string>(getInitialTab);
    const [taskStateTab, setTaskStateTab] = useState(getInitialStateTab); // 'open', 'closed', 'all'

    const [settingsSubTabs, setSettingsSubTabs] = useState([
        {
            id: "sprints",
            label: t("tasks.settings_subtabs.sprints"),
            icon: <Icon name="repeat" className="w-4 h-4" />,
        },
        {
            id: "statuses",
            label: t("tasks.settings_subtabs.statuses"),
            icon: <Icon name="layoutTemplate" className="w-4 h-4" />,
        },
    ]);

    useEffect(() => {
        const path = location.pathname.replace(/\/+$/, "");
        const seg = path.split("/").pop() || "";
        switch (seg) {
            case "kanban":
                setActiveTab("kanban");
                break;
            case "settings":
                setActiveTab("settings");
                break;
            case "sprints":
            case "statuses":
                setActiveTab("settings");
                setSettingsSubTab(seg as "sprints" | "statuses");
                break;
            case "table":
            case "tasks":
                setActiveTab("table");
                break;
            default:
                break;
        }
    }, [location.pathname]);

    const [statusFilter, setStatusFilter] = useStoredPreference(
        "vigepra_tasks_status",
        z.array(z.string()),
        [],
        true
    );
    const [assigneeFilter, setAssigneeFilter] = useStoredPreference(
        "vigepra_tasks_assignees",
        z.array(z.string()),
        [],
        true
    );
    const [search, setSearch] = useStoredPreference(
        "vigepra_tasks_search",
        z.string().max(500),
        "",
        true
    );
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskToEdit, setTaskToEdit] = useState<any | null>(null);
    const [priorityTarget, setPriorityTarget] = useState<any | null>(null);
    const [assigneeTarget, setAssigneeTarget] = useState<any | null>(null);

    const handlePrioritySuccess = (updatedTask: any): void => {
        setPriorityTarget(null);
        setTasks((prev) =>
            prev.map((t) => {
                if (t.id === updatedTask.id) {
                    return { ...t, priority: updatedTask.priority };
                }
                return t;
            })
        );
    };

    const handleAssigneeSuccess = (updatedTask: any): void => {
        setAssigneeTarget(null);
        setTasks((prev) =>
            prev.map((t) => {
                if (t.id === updatedTask.id) {
                    return {
                        ...t,
                        assignee: updatedTask.assignee,
                        assignee_id: updatedTask.assignee_id,
                    };
                }
                return t;
            })
        );
    };

    const fetchStatuses = async () => {
        try {
            const res = await taskStatusesApi.list();
            if (res.result) setStatuses(res.data);
        } catch (err) {
            console.error("Error reloading statuses", err);
        }
    };

    const fetchTasks = async () => {
        try {
            const res = await tasksApi.list();
            if (res.result) setTasks(res.data);
        } catch (err) {
            //console.error("Error loading tasks", err);
            showError(t("tasks.error_loading"));
        }
    };

    const fetchActiveSprint = async (): Promise<void> => {
        try {
            const res = await sprintsApi.active();
            if (res.result) {
                setActiveSprint(res.data);
            } else {
                setActiveSprint(null);
            }
        } catch {
            setActiveSprint(null);
        }
    };

    const fetchData = async (showLoading = true): Promise<void> => {
        if (showLoading) {
            setLoading(true);
        }
        await Promise.all([fetchStatuses(), fetchTasks(), fetchActiveSprint()]);
        if (showLoading) {
            setLoading(false);
        }
        window.dispatchEvent(new CustomEvent("tasks_updated"));
    };

    useEffect(() => {
        fetchData();
    }, []);

    const isDraggingRef = useRef<boolean>(false);
    const dragEndedAtRef = useRef<number>(0);

    const onDragStart = (): void => {
        isDraggingRef.current = true;
    };

    const onDragEnd = async (result: any): Promise<void> => {
        dragEndedAtRef.current = Date.now();
        setTimeout(() => {
            isDraggingRef.current = false;
        }, 250);

        const { source, destination, draggableId } = result;
        if (!destination) {
            return;
        }

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        if (result.type === "column") {
            const visibleStatuses = statuses.filter((s) => !s.is_hidden);
            const hiddenStatuses = statuses.filter((s) => s.is_hidden);

            const newVisible = Array.from(visibleStatuses);
            const [movedCol] = newVisible.splice(source.index, 1);
            newVisible.splice(destination.index, 0, movedCol);

            const newStatuses = [...newVisible, ...hiddenStatuses];
            setStatuses(newStatuses);

            const reorderedData = newStatuses.map((status, idx) => ({
                id: status.id,
                order: idx + 1,
            }));
            try {
                await taskStatusesApi.reorder({ statuses: reorderedData });
                fetchStatuses();
            } catch (error) {
                //console.error("Error reordering columns:", error);
                showError(t("tasks.error_reorder_columns"));
            }
            return;
        }

        const sourceStatusId = parseInt(source.droppableId);
        const destStatusId = parseInt(destination.droppableId);
        const taskId = parseInt(draggableId);

        if (sourceStatusId === destStatusId) {
            const currentColumnTasks = Array.from(
                filteredTasksForKanban.filter(
                    (t) => t.status_id === sourceStatusId
                )
            );
            const movedIndex = currentColumnTasks.findIndex(
                (t) => t.id === taskId
            );
            if (movedIndex === -1) {
                return;
            }

            const [movedItem] = currentColumnTasks.splice(movedIndex, 1);
            currentColumnTasks.splice(destination.index, 0, movedItem);

            const reorderedData = currentColumnTasks.map((t, idx) => ({
                id: t.id,
                status_id: sourceStatusId,
                sort_order: idx,
            }));

            const payloadMap = new Map(
                reorderedData.map((item) => [item.id, item])
            );

            const updatedTasks = tasks.map((t) => {
                const item = payloadMap.get(t.id);
                if (item) {
                    return { ...t, sort_order: item.sort_order };
                }
                return t;
            });

            setTasks(updatedTasks);

            try {
                await tasksApi.reorder({ tasks: reorderedData });
            } catch (err) {
                console.error("Reorder failed", err);
                showError(t("tasks.error_reorder"));
            }
        } else {
            const sourceColumnTasks = Array.from(
                filteredTasksForKanban.filter(
                    (t) => t.status_id === sourceStatusId
                )
            );
            const destColumnTasks = Array.from(
                filteredTasksForKanban.filter(
                    (t) => t.status_id === destStatusId
                )
            );

            const movedIndex = sourceColumnTasks.findIndex(
                (t) => t.id === taskId
            );
            if (movedIndex === -1) {
                return;
            }

            const [movedItem] = sourceColumnTasks.splice(movedIndex, 1);
            const targetStatus = statuses.find((s) => s.id === destStatusId);
            const movedWithStatus = {
                ...movedItem,
                status_id: destStatusId,
                status: targetStatus || movedItem.status,
            };
            destColumnTasks.splice(destination.index, 0, movedWithStatus);

            const sourceReordered = sourceColumnTasks.map((t, idx) => ({
                id: t.id,
                status_id: sourceStatusId,
                sort_order: idx,
            }));

            const destReordered = destColumnTasks.map((t, idx) => ({
                id: t.id,
                status_id: destStatusId,
                sort_order: idx,
            }));

            const allReordered = [...sourceReordered, ...destReordered];
            const payloadMap = new Map(
                allReordered.map((item) => [item.id, item])
            );

            const updatedTasks = tasks.map((t) => {
                const item = payloadMap.get(t.id);
                if (item) {
                    return {
                        ...t,
                        status_id: item.status_id,
                        sort_order: item.sort_order,
                        status:
                            statuses.find((s) => s.id === item.status_id) ||
                            t.status,
                    };
                }
                return t;
            });

            setTasks(updatedTasks);

            try {
                await tasksApi.reorder({ tasks: allReordered });
            } catch (err) {
                console.error("Move failed", err);
                showError(t("tasks.error_reorder"));
            }
        }
    };

    const handleTaskSaved = (savedTask: any): void => {
        if (taskToEdit) {
            setTasks((prev) =>
                prev.map((t) =>
                    t.id === savedTask.id ? { ...t, ...savedTask } : t
                )
            );
            setTaskToEdit(null);
        } else {
            setTasks((prev) => [savedTask, ...prev]);
            setIsNewTaskModalOpen(false);
        }
        fetchTasks();
    };

    const handleOpenNewTask = (
        statusId: number | null = null,
        sprintId: number | null = null
    ) => {
        setPrefilledStatusId(statusId);
        setPrefilledSprintId(
            sprintId ?? (activeSprint ? activeSprint.id : null)
        );
        setIsNewTaskModalOpen(true);
    };

    const stats = useMemo(() => {
        const open = tasks.filter(
            (t) => t.status?.tag !== "done" && t.status?.tag !== "cancelled"
        );
        const assigned = open.filter(
            (t) =>
                Number(t.assignee_id) === Number(user?.id) ||
                Number(t.assignee?.id) === Number(user?.id)
        ).length;
        const closed = tasks.filter(
            (t) => t.status?.tag === "done" || t.status?.tag === "cancelled"
        ).length;
        return { open: open.length, assigned, closed, total: tasks.length };
    }, [tasks, user]);

    const baseFilteredTasks = useMemo(() => {
        let result = tasks;

        if (statusFilter.length > 0) {
            result = result.filter((t) =>
                statusFilter.includes(String(t.status_id))
            );
        }

        if (assigneeFilter.length > 0) {
            const hasUnassigned = assigneeFilter.includes("unassigned");
            const userIds = assigneeFilter.filter((id) => id !== "unassigned");
            result = result.filter((t) => {
                if (!t.assignee_id && hasUnassigned) {
                    return true;
                }
                if (t.assignee_id && userIds.includes(String(t.assignee_id))) {
                    return true;
                }
                return false;
            });
        }

        if (search) {
            const s = search.toLowerCase();
            result = result.filter((t) => {
                const code = t.code?.toLowerCase() || "";
                const title = t.title?.toLowerCase() || "";
                const desc = t.description?.toLowerCase() || "";
                return (
                    code.includes(s) || title.includes(s) || desc.includes(s)
                );
            });
        }

        return [...result].sort((a, b) => {
            const orderA = a.sort_order ?? 0;
            const orderB = b.sort_order ?? 0;
            if (orderA !== orderB) {
                return orderA - orderB;
            }
            return b.id - a.id;
        });
    }, [tasks, statusFilter, assigneeFilter, search]);

    const filteredTasksForKanban = useMemo(() => {
        if (!activeSprint) {
            return [];
        }
        return baseFilteredTasks.filter((t) => t.sprint_id === activeSprint.id);
    }, [baseFilteredTasks, activeSprint]);

    const filteredTasksForTable = useMemo(() => {
        let result = baseFilteredTasks;

        if (taskStateTab === "open") {
            result = result.filter(
                (t) => t.status?.tag !== "done" && t.status?.tag !== "cancelled"
            );
        } else {
            if (taskStateTab === "closed") {
                result = result.filter(
                    (t) =>
                        t.status?.tag === "done" ||
                        t.status?.tag === "cancelled"
                );
            }
        }

        return result;
    }, [baseFilteredTasks, taskStateTab]);

    const statusOptions = useMemo(() => {
        return statuses.map((s) => ({
            value: String(s.id),
            label: t(`tasks.statuses.${s.tag}`) || s.name,
            icon: () => (
                <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: s.color || "#cbd5e1" }}
                />
            ),
        }));
    }, [statuses, t]);

    const assigneeOptions = useMemo(() => {
        const uniqueAssignees: { value: string; label: string }[] = [];
        const map = new Map<number, boolean>();
        tasks.forEach((t) => {
            if (t.assignee && !map.has(t.assignee.id)) {
                map.set(t.assignee.id, true);
                uniqueAssignees.push({
                    value: String(t.assignee.id),
                    label: t.assignee.name,
                });
            }
        });
        uniqueAssignees.sort((a, b) => a.label.localeCompare(b.label));
        return [
            { value: "unassigned", label: t("tasks.table.unassigned") },
            ...uniqueAssignees,
        ];
    }, [tasks, t]);

    const taskCounts = useMemo(() => {
        const open = baseFilteredTasks.filter(
            (t) => t.status?.tag !== "done" && t.status?.tag !== "cancelled"
        ).length;
        const closed = baseFilteredTasks.filter(
            (t) => t.status?.tag === "done" || t.status?.tag === "cancelled"
        ).length;
        const all = baseFilteredTasks.length;
        return { open, closed, all };
    }, [baseFilteredTasks]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
            </div>
        );
    }

    const tabs = [
        {
            id: "table",
            label: t("tasks.tabs.table"),
            icon: <Icon name="table" className="w-4 h-4" />,
        },
        {
            id: "kanban",
            label: t("tasks.tabs.kanban"),
            icon: <Icon name="kanbanSquare" className="w-4 h-4" />,
        },
        {
            id: "settings",
            label: t("tasks.tabs.settings"),
            icon: <Icon name="settings" className="w-4 h-4" />,
        },
    ];

    const taskStateTabs = [
        {
            id: "open",
            label: t("tasks.kpi_open"),
            icon: <Icon name="clock" className="w-4 h-4" />,
            badge: taskCounts.open,
        },
        {
            id: "closed",
            label: t("tasks.kpi_closed"),
            icon: <Icon name="checkCircle" className="w-4 h-4" />,
            badge: taskCounts.closed,
        },
        {
            id: "all",
            label: t("tasks.kpi_total"),
            icon: <Icon name="layers" className="w-4 h-4" />,
            badge: taskCounts.all,
        },
    ];

    const taskFilters = (
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto flex-1">
            <Select
                multiple
                clearable
                searchable
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
                placeholder={t("tasks.all_statuses")}
                className="w-full sm:flex-1 sm:min-w-0 lg:flex-initial lg:w-[220px]"
            />
            <Select
                multiple
                clearable
                searchable
                value={assigneeFilter}
                onChange={setAssigneeFilter}
                options={assigneeOptions}
                placeholder={t("tasks.all_assignees")}
                className="w-full sm:flex-1 sm:min-w-0 lg:flex-initial lg:w-[220px]"
            />
        </div>
    );

    const filterBar = (
        <div className="flex flex-col sm:flex-row items-center gap-3 bg-white dark:bg-zinc-900 p-3 rounded-2xl border border-slate-200/80 dark:border-zinc-800 shadow-sm shrink-0">
            <div className="w-full">
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t("tasks.search_placeholder")}
                />
            </div>
            <div className="w-full sm:w-auto ml-auto">{taskFilters}</div>
        </div>
    );

    return (
        <div className="page space-y-3">
            <Tabs
                className="my-4"
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                variant="underline"
                urlPrefix="/tasks"
                urlDefaultTab="table"
            />

            {/* Kanban: Filters at the top */}
            {activeTab === "kanban" && (
                <div className="mb-4 shrink-0">{filterBar}</div>
            )}

            {/* Page Header */}
            {activeTab === "table" && (
                <PageHeader
                    compact
                    className="!mb-0"
                    title={t("tasks.title")}
                    action={{
                        label: t("tasks.new_task"),
                        icon: "plus",
                        onClick: () => handleOpenNewTask(),
                    }}
                />
            )}

            {/* KPI Cards are only shown in Table view */}
            {/*activeTab === 'table' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 shrink-0">
                    
                    <StatCard
                        title={t('tasks.kpi_assigned') || 'Asignadas'}
                        value={stats.assigned}
                        icon="user"
                        colorClass='transition-colors text-amber-600 dark:text-amber-400'
                        valueColorClass={`transition-colors text-amber-600 dark:text-amber-400`}
                        onClick={() => { 
                            if(user) {
                                setAssigneeFilter([String(user.id)]); 
                                setTaskStateTab('open');
                            }
                        }}
                    />
                    <StatCard
                        title={t('tasks.kpi_open')}
                        value={stats.open}
                        icon="clock"
                        colorClass='transition-colors text-rose-600 dark:text-rose-400'
                        valueColorClass={`transition-colors text-rose-600 dark:text-rose-400`}
                        onClick={() => { setTaskStateTab('open'); setStatusFilter([]); setAssigneeFilter([]); }}
                    />
                    <StatCard
                        title={t('tasks.kpi_closed')}
                        value={stats.closed}
                        icon="checkCircle"
                        colorClass='transition-colors text-emerald-600 dark:text-emerald-400'
                        valueColorClass={`transition-colors text-emerald-600 dark:text-emerald-400`}
                        onClick={() => { setTaskStateTab('closed'); setStatusFilter([]); setAssigneeFilter([]); }}
                    />
                    <StatCard
                        title={t('tasks.kpi_total')}
                        value={stats.total}
                        icon="layers"
                        colorClass='transition-colors text-slate-700 dark:text-slate-200'
                        valueColorClass={`transition-colors text-slate-700 dark:text-slate-200`}
                        onClick={() => { setTaskStateTab('all'); setStatusFilter([]); setAssigneeFilter([]); }}
                    />
                </div>
            )*/}

            {/* Table: KPI tabs */}
            {activeTab === "table" && (
                <div className="w-full mb-6">
                    <Tabs
                        tabs={taskStateTabs}
                        activeTab={taskStateTab}
                        onChange={setTaskStateTab}
                        variant="underline"
                        syncUrl={true}
                        urlPrefix={`/tasks/${activeTab}`}
                    />
                </div>
            )}

            {activeTab === "kanban" ? (
                <div className="flex flex-col flex-1 overflow-hidden">
                    {activeSprint ? (
                        <div className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar">
                            <DragDropContext
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                            >
                                <Droppable
                                    droppableId="board"
                                    type="column"
                                    direction="horizontal"
                                >
                                    {(provided) => (
                                        <div
                                            className="flex gap-3 h-full pb-0 items-start w-full min-w-full"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {statuses
                                                .filter((s) => !s.is_hidden)
                                                .map((status, index) => {
                                                    const columnTasks =
                                                        filteredTasksForKanban.filter(
                                                            (t) =>
                                                                t.status_id ===
                                                                status.id
                                                        );
                                                    return (
                                                        <KanbanColumn
                                                            key={status.id}
                                                            status={status}
                                                            tasks={columnTasks}
                                                            onAddTask={
                                                                handleOpenNewTask
                                                            }
                                                            onTaskClick={
                                                                setSelectedTask
                                                            }
                                                            onPriorityClick={
                                                                setPriorityTarget
                                                            }
                                                            onAssigneeClick={
                                                                setAssigneeTarget
                                                            }
                                                            onSprintClick={() => {
                                                                setActiveTab(
                                                                    "settings"
                                                                );
                                                                setSettingsSubTab(
                                                                    "sprints"
                                                                );
                                                                navigate(
                                                                    "/tasks/settings/sprints"
                                                                );
                                                            }}
                                                            index={index}
                                                            isDraggingRef={
                                                                isDraggingRef
                                                            }
                                                            dragEndedAtRef={
                                                                dragEndedAtRef
                                                            }
                                                        />
                                                    );
                                                })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-2xl shadow-xs text-center my-auto">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                                <Icon name="play" className="w-8 h-8" />
                            </div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">
                                {t("tasks.sprints.no_active")}
                            </h3>
                            <div className="text-sm text-slate-500 dark:text-zinc-400 max-w-md mb-6">
                                {t("tasks.sprints.no_active_desc")}
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setActiveTab("settings");
                                    setSettingsSubTab("sprints");
                                    navigate("/tasks/settings/sprints");
                                }}
                                className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 shadow-xs cursor-pointer"
                            >
                                <span>{t("tasks.sprints.start")}</span>
                            </button>
                        </div>
                    )}
                </div>
            ) : activeTab === "settings" ? (
                <div className="flex-1">
                    <Tabs
                        subtabs={true}
                        full={false}
                        tabs={settingsSubTabs}
                        activeTab={settingsSubTab}
                        onChange={setSettingsSubTab}
                        syncUrl={true}
                        urlPrefix="/tasks/settings"
                        urlDefaultTab="sprints"
                        className="mb-2"
                    />

                    {settingsSubTab === "sprints" ? (
                        <SprintsSettings
                            onSprintChanged={() => fetchData(false)}
                        />
                    ) : (
                        <TaskStatusesSettings
                            onStatusesUpdated={() => fetchData(false)}
                        />
                    )}
                </div>
            ) : (
                <div className="flex-1">
                    <TasksTable
                        tasks={filteredTasksForTable}
                        search={search}
                        onSearchChange={setSearch}
                        viewFilters={{
                            statuses: JSON.stringify(statusFilter),
                            assignees: JSON.stringify(assigneeFilter),
                        }}
                        viewContext={taskStateTab}
                        onRestoreView={(filters, context) => {
                            setStatusFilter(
                                parseListSelection(filters.statuses)
                            );
                            setAssigneeFilter(
                                parseListSelection(filters.assignees)
                            );
                            if (["open", "closed", "all"].includes(context)) {
                                setTaskStateTab(context);
                            }
                        }}
                        filters={taskFilters}
                        onRowClick={(row) => setSelectedTask(row)}
                        onEditClick={(row) => setTaskToEdit(row)}
                        onRefresh={fetchTasks}
                    />
                </div>
            )}

            <NewTaskModal
                isOpen={isNewTaskModalOpen || taskToEdit !== null}
                onClose={() => {
                    setIsNewTaskModalOpen(false);
                    setTaskToEdit(null);
                }}
                task={taskToEdit}
                onSuccess={handleTaskSaved}
                statuses={statuses}
                prefilledStatusId={prefilledStatusId}
                prefilledSprintId={prefilledSprintId}
            />

            <TaskViewModal
                task={selectedTask}
                isOpen={!!selectedTask}
                onClose={(hasChanged) => {
                    setSelectedTask(null);
                    if (hasChanged) {
                        fetchTasks();
                    }
                }}
            />

            <ChangeTaskPriorityModal
                isOpen={priorityTarget !== null}
                task={priorityTarget}
                onClose={() => setPriorityTarget(null)}
                onSuccess={handlePrioritySuccess}
            />

            <ChangeTaskAssigneeModal
                isOpen={assigneeTarget !== null}
                task={assigneeTarget}
                onClose={() => setAssigneeTarget(null)}
                onSuccess={handleAssigneeSuccess}
            />
        </div>
    );
};
